import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import * as performanceAction from '../actions/performance.action';
import {
  LOAD_PERFORMANCE_TEST,
  LOAD_PERFORMANCES_HISTORY,
  LoadPerformancesHistory,
  LoadPerformanceTest
} from '../actions/performance.action';
import {DbResponse} from "../../shared/interceptors/db-response";
import {filter, map, mergeMap, switchMap} from 'rxjs/operators';
import {Utils} from "../../shared/utils/utils";
import {PerformanceService} from "../../websites/website/performance/performance.service";
import * as ongoingActivityActions from '../actions';
import {
  AddActivity,
  FinishOngoingActivity,
  RUN_PERFORMANCE_TEST,
  RUN_PERFORMANCE_TEST_SUCCESS,
  RunPerformanceTest,
  RunPerformanceTestSuccess,
  UpdateOngoingActivityState
} from '../actions';
import {ActivityAction, ActivityStatus, ActivityType, IActivity} from '../../activities/activity';
import {DbStoreState} from "../reducers";
import {Store} from "@ngrx/store";
import * as activityActions from "../actions/activities.action";

@Injectable()
export class PerformanceEffect {

  private readonly _pendingActivityMap: {[activityId: string]: IActivity};
  private readonly _simulatingActivityMap: {[activityId: string]: IActivity};

  constructor(
    private _store: Store<DbStoreState>,
    private _actions$: Actions,
    private _performanceService: PerformanceService
  ) {
    this._pendingActivityMap = {};
    this._simulatingActivityMap = {};
  }

 /* @Effect() updateWebsiteSubData$ = this._actions$.ofType(UPDATE_WEBSITE_SUB_DATA)
    .pipe(
      map((action: UpdateWebsiteSubData) => {
        return new performanceAction.LoadPerformancesHistory(action.payload.id);
      })
    );*/

  loadPerformancesHistory$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_PERFORMANCES_HISTORY),
    mergeMap((action: LoadPerformancesHistory) => {
      return this._performanceService.getPerformanceHistory(action.payload)
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new performanceAction.LoadPerformancesHistorySuccess({
                websiteId: action.payload,
                entities: Utils.arrayToEntities(response.data, 'id')
              });
            }
            return new performanceAction.LoadPerformancesHistoryFail(response.message);
          })
        );
    })
  ));

  loadPerformanceTest$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_PERFORMANCE_TEST),
    switchMap((action: LoadPerformanceTest) => {
      const websiteId: number = action.payload.websiteId;
      const performanceTestId: string = action.payload.performanceTestId;
      return this._performanceService.getPerformanceTest(websiteId, performanceTestId)
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new performanceAction.LoadPerformanceTestSuccess({
                websiteId: websiteId,
                performanceTestId: performanceTestId,
                performanceTest: response.data
              });
            }
            return new performanceAction.LoadPerformanceTestFail({
              websiteId: websiteId,
              performanceTestId: performanceTestId,
              error: response.message
            });
          })
        );
    })
  ));

  runPerformanceTest$ = createEffect(() => this._actions$.pipe(
    ofType(RUN_PERFORMANCE_TEST),
    map((action: RunPerformanceTest) => {
      const websiteId: number = action.payload;
      const relativeUrl: string = action.relativeUrl;
      const activityId: string = websiteId + 'RunPerformance';

      this._performanceService.runPerformanceScan(websiteId, relativeUrl)
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (!response.success) {
              setTimeout(() => {
                delete this._pendingActivityMap[activityId];

                this._store.dispatch(new UpdateOngoingActivityState({
                  websiteId: websiteId,
                  entity: this.generatePerformanceTestFailedActivity(activityId, websiteId)
                }));
                this._store.dispatch(new FinishOngoingActivity({
                  websiteId: websiteId,
                  entity: this.generatePerformanceTestFailedActivity(activityId, websiteId)
                }));
              });
            }
          })
        );

      const activity: IActivity = this.generatePerformanceTestPendingActivity(activityId, websiteId);

      this._pendingActivityMap[activityId] = activity;

      return new ongoingActivityActions.UpdateOngoingActivityState({
        websiteId: websiteId,
        entity: activity
      });
    })
  ));

  startPerformanceTestActivitySimulation$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityAction.PERFORMANCE_CHECK
    ]).includes(action.payload.action)),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.RUNNING
    ]).includes(action.payload.status)),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + 'RunPerformance';
      return !!this._pendingActivityMap[activityId];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + 'RunPerformance';

      delete this._pendingActivityMap[activityId];

      if (activity.progress === 0) {
        this.simulateActivityProgress(activity);
      }

      return new ongoingActivityActions.RemoveOngoingActivity({
        websiteId: activity.websiteId,
        activityId: activityId
      });
    })
  ));

  stopWebsiteProductActivitySimulation$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityAction.PERFORMANCE_CHECK
    ]).includes(action.payload.action)),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.DONE,
      ActivityStatus.FAILED
    ]).includes(action.payload.status)),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      return !!this._simulatingActivityMap[activity.id];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      delete this._simulatingActivityMap[activity.id];

      return new performanceAction.RunPerformanceTestSuccess({
        websiteId: activity.websiteId,
        performanceTestId: activity.id,
        performanceTest: activity.meta
      });
    })
  ));

  runPerformanceTestSuccessReloadHistory$ = createEffect(() => this._actions$.pipe(
    ofType(RUN_PERFORMANCE_TEST_SUCCESS),
    map((action: RunPerformanceTestSuccess) => {
      const websiteId: number = action.payload.websiteId;
      return new performanceAction.LoadPerformancesHistory(websiteId);
    })
  ));



  private simulateActivityProgress(baseActivity: IActivity): void {
    this._simulatingActivityMap[baseActivity.id] = baseActivity;

    const interval = setInterval((): void => {
      const previousActivity: IActivity = this._simulatingActivityMap[baseActivity.id];

      if (!previousActivity || previousActivity.progress >= 98) {
        clearInterval(interval);
        return;
      }

      let nextProgress: number;
      if (previousActivity.progress < 50) {
        nextProgress = previousActivity.progress + 3;
      }
      else if (previousActivity.progress < 80) {
        nextProgress = previousActivity.progress + 2;
      }
      else {
        nextProgress = previousActivity.progress + 1;
      }

      const activity: IActivity = <IActivity>{};
      activity.id = previousActivity.id;
      activity.websiteId = previousActivity.websiteId;
      activity.service = previousActivity.service;
      activity.type = previousActivity.type;
      activity.action = previousActivity.action;
      activity.date = new Date();
      activity.status = previousActivity.status;
      activity.progress = nextProgress;
      activity.message = previousActivity.message;
      activity.meta = JSON.parse(JSON.stringify(previousActivity.meta));

      this._simulatingActivityMap[activity.id] = activity;

      this._store.dispatch(new UpdateOngoingActivityState({
        websiteId: activity.websiteId,
        entity: activity
      }));
    }, 500);
  }

  private generatePerformanceTestPendingActivity(activityId: string, websiteId: number): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = websiteId;
    activity.service = 'performance';
    activity.type = ActivityType.ACTIVITY;
    activity.action = ActivityAction.PERFORMANCE_CHECK;
    activity.date = new Date();
    activity.status = ActivityStatus.PENDING;
    activity.progress = 0;
    activity.message = '';
    activity.meta = null;

    return activity;
  }

  private generatePerformanceTestFailedActivity(activityId: string, websiteId: number): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = websiteId;
    activity.service = 'performance';
    activity.type = ActivityType.ACTIVITY;
    activity.action = ActivityAction.PERFORMANCE_CHECK;
    activity.date = new Date();
    activity.status = ActivityStatus.FAILED;
    activity.progress = 100;
    activity.message = '';
    activity.meta = null;

    return activity;
  }

}
